import React, { useEffect, useMemo, useState, useRef } from "react";
import api, { inr } from "../../lib/api";
import { toast } from "sonner";
import { Search, Printer, Download, Zap } from "lucide-react";
import OrderDetailsDrawer from "../../components/OrderDetailsDrawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const STATUSES = ["pending", "processing", "ready_for_dispatch", "shipped", "delivered", "cancelled"];
const LEGACY_STATUS_MAP = {
  placed: "pending",
  confirmed: "processing",
  out_for_delivery: "shipped",
};
const normalizeStatus = (s) => LEGACY_STATUS_MAP[s] || s || "pending";
const PAYMENT_STATUSES = [
  { value: "paid", label: "Paid" },
  { value: "created", label: "Pending" },
  { value: "cod", label: "COD" },
  { value: "cancelled", label: "Cancelled" },
];

const derivePaymentStatus = (o) => {
  if (o.status === "cancelled" || o.tracking_status === "cancelled") return "cancelled";
  if (o.payment_method === "cod") return "cod";
  return o.status === "paid" ? "paid" : "created";
};

const PAYMENT_PILL = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  created: "bg-amber-50 text-amber-700 border-amber-200",
  cod: "bg-sky-50 text-sky-700 border-sky-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};
const PAYMENT_LABEL = { paid: "Paid", created: "Pending", cod: "COD", cancelled: "Cancelled" };

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [search, setSearch] = useState("");
  const [unclaimedOnly, setUnclaimedOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelVerifiedVia, setCancelVerifiedVia] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Reads ?unclaimed=true from the URL once on mount — this is what the
  // Dashboard's "New Orders" card links to.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("unclaimed") === "true") setUnclaimedOnly(true);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set("tracking_status", filter);
      if (unclaimedOnly) params.set("unclaimed", "true");
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const qs = params.toString();
      const { data } = await api.get(`/admin/orders${qs ? `?${qs}` : ""}`);
      setOrders(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter, unclaimedOnly, dateFrom, dateTo]);

  // Deep-link support: /admin/orders?focus=<id> auto-opens that order's
  // drawer — this is what the Dashboard's "Recent activity" links to.
  const appliedFocusRef = useRef(false);
  useEffect(() => {
    if (appliedFocusRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    if (!focusId || orders.length === 0) return;
    const match = orders.find((o) => o.id === focusId);
    if (match) {
      setSelectedOrder(match);
      appliedFocusRef.current = true;
      // Strip the param immediately — otherwise every later orders refresh
      // (claim, scan, status change) re-runs this effect, finds the same
      // stale focus id still in the URL, and snaps the drawer back to it,
      // silently discarding whatever order the person had since opened.
      const url = new URL(window.location.href);
      url.searchParams.delete("focus");
      window.history.replaceState({}, "", url);
    }
  }, [orders]);

  const updateStatus = async (orderId, status, extra = {}) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { tracking_status: status, ...extra });
      toast.success(`Order updated to ${status.replace(/_/g, " ")}`);
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, tracking_status: status } : o));
      setSelectedOrder((prev) => (prev && prev.id === orderId ? { ...prev, tracking_status: status } : prev));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update order");
    }
  };

  const requestStatusChange = (order, status) => {
    if (status === "cancelled") {
      setCancelTarget(order);
      setCancelVerifiedVia("");
      setCancelReason("");
      return;
    }
    updateStatus(order.id, status);
  };

  const confirmCancel = async () => {
    if (!cancelVerifiedVia.trim() || !cancelReason.trim()) {
      toast.error("Enter both a verification contact and a reason");
      return;
    }
    setCancelling(true);
    try {
      await updateStatus(cancelTarget.id, "cancelled", {
        verified_via: cancelVerifiedVia.trim(),
        reason: cancelReason.trim(),
      });
      setCancelTarget(null);
    } finally {
      setCancelling(false);
    }
  };

  const claimOrder = async (order) => {
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/claim`);
      toast.success("Order claimed");
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      setSelectedOrder((prev) => (prev && prev.id === order.id ? data : prev));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't claim this order");
    }
  };

  const onOrderUpdate = (updated) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setSelectedOrder(updated);
  };

  // Client-side search also matches who claimed an order — a lightweight
  // "claimed by" filter without needing a separate staff-list permission.
  const visibleOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (paymentFilter && derivePaymentStatus(o) !== paymentFilter) return false;
      if (!q) return true;
      const hay = [o.id, o.customer?.name, o.customer?.email, o.customer?.phone, o.claimed_by_name].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [orders, search, paymentFilter]);

  return (
    <div data-testid="admin-orders-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-2">Manage</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Orders</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-welljio-muted pointer-events-none" strokeWidth={2} />
            <input
              type="search"
              data-testid="orders-search-input"
              placeholder="Search order, name, email, claimed by…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-full border border-slate-200 pl-10 pr-4 py-2.5 text-sm bg-white w-64 max-w-full focus:outline-none focus:ring-2 focus:ring-welljio-accent/30 focus:border-welljio-accent transition-colors"
            />
          </div>
          <select
            data-testid="orders-payment-filter"
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="rounded-full border border-slate-200 px-5 py-2.5 text-sm bg-white"
          >
            <option value="">All payments</option>
            {PAYMENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            data-testid="orders-status-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-full border border-slate-200 px-5 py-2.5 text-sm bg-white"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-100 rounded-2xl px-4 py-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={unclaimedOnly}
            onChange={(e) => setUnclaimedOnly(e.target.checked)}
            data-testid="orders-unclaimed-toggle"
            className="accent-welljio-coral"
          />
          <span className={`inline-flex items-center gap-1 font-medium ${unclaimedOnly ? "text-welljio-coral" : "text-welljio-ink"}`}>
            <Zap size={13} strokeWidth={2}/> Unclaimed only
          </span>
        </label>
        <div className="w-px h-5 bg-slate-200" />
        <label className="text-xs text-welljio-muted flex items-center gap-2">
          From
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="orders-date-from"
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs"
          />
        </label>
        <label className="text-xs text-welljio-muted flex items-center gap-2">
          To
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="orders-date-to"
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs"
          />
        </label>
        {(dateFrom || dateTo || unclaimedOnly) && (
          <button
            onClick={() => { setDateFrom(""); setDateTo(""); setUnclaimedOnly(false); }}
            data-testid="orders-clear-filters"
            className="text-xs text-welljio-accent hover:underline ml-auto"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-welljio-muted">Loading orders...</p>
      ) : visibleOrders.length === 0 ? (
        <p className="text-welljio-muted" data-testid="orders-empty-state">No orders match the current filters.</p>
      ) : (
        <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[980px]">
              <thead className="bg-welljio-tint">
                <tr className="text-left">
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Order ID</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Customer Name</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Total</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Payment Status</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Claimed By</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Order Status</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap">Date</th>
                  <th className="px-5 py-3 font-medium text-welljio-muted whitespace-nowrap text-right">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((o) => {
                  const pay = derivePaymentStatus(o);
                  return (
                    <tr
                      key={o.id}
                      data-testid={`admin-order-row-${o.id}`}
                      onClick={() => setSelectedOrder(o)}
                      className="border-t border-slate-100 hover:bg-welljio-tint/40 transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-4 font-mono text-xs whitespace-nowrap">{o.id.slice(-10)}</td>
                      <td className="px-5 py-4">
                        <p className="font-medium">{o.customer?.name || "Guest"}</p>
                        <p className="text-xs text-welljio-muted">{o.customer?.email}</p>
                      </td>
                      <td className="px-5 py-4 font-medium whitespace-nowrap">{o.total !== undefined ? inr(o.total) : <span className="text-welljio-subtle">—</span>}</td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span
                          data-testid={`order-payment-status-${o.id}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide ${PAYMENT_PILL[pay]}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />
                          {PAYMENT_LABEL[pay]}
                        </span>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {o.claimed_by_name ? (
                          <span className="text-xs font-medium text-welljio-ink">{o.claimed_by_name}</span>
                        ) : o.tracking_status === "cancelled" ? (
                          <span className="text-xs text-welljio-subtle">—</span>
                        ) : (
                          <button onClick={() => claimOrder(o)} data-testid={`order-claim-${o.id}`} className="text-xs font-medium text-welljio-accent hover:underline">
                            Claim
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                        <select
                          data-testid={`order-status-select-${o.id}`}
                          value={normalizeStatus(o.tracking_status)}
                          onChange={(e) => requestStatusChange(o, e.target.value)}
                          className="rounded-full border border-slate-200 px-3 py-1.5 text-xs bg-white capitalize"
                        >
                          {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-4 text-welljio-muted whitespace-nowrap">
                        {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <a href={`/invoice/${o.id}?autoprint=1`} target="_blank" rel="noreferrer" data-testid={`order-print-${o.id}`} title="Print invoice" aria-label="Print invoice" className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-200 bg-white text-welljio-ink hover:bg-welljio-tint hover:border-welljio-accent/40 transition-colors">
                            <Printer size={13} strokeWidth={2.2} />
                          </a>
                          <a href={`/invoice/${o.id}?autoprint=1`} target="_blank" rel="noreferrer" data-testid={`order-download-${o.id}`} title="Download PDF" aria-label="Download PDF" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-welljio-accent text-white hover:opacity-90 transition-opacity">
                            <Download size={13} strokeWidth={2.2} />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <OrderDetailsDrawer
        order={selectedOrder}
        open={!!selectedOrder}
        onOpenChange={(open) => { if (!open) setSelectedOrder(null); }}
        onStatusChange={(orderId, status) => {
          const order = orders.find((o) => o.id === orderId);
          if (order) requestStatusChange(order, status);
        }}
        onOrderUpdate={onOrderUpdate}
      />

      <Dialog open={!!cancelTarget} onOpenChange={(open) => { if (!open) setCancelTarget(null); }}>
        <DialogContent data-testid="cancel-order-dialog" className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Cancel this order?</DialogTitle>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-3 mt-2">
              <p className="text-sm text-welljio-muted">
                Confirm this request matches the contact on file for order {cancelTarget.id.slice(-10)}:
                {" "}{cancelTarget.customer?.email || cancelTarget.customer?.phone}
              </p>
              <input
                value={cancelVerifiedVia}
                onChange={(e) => setCancelVerifiedVia(e.target.value)}
                placeholder="Email or phone the customer used to request this"
                data-testid="cancel-verified-via"
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"
              />
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Reason for cancellation"
                rows={3}
                data-testid="cancel-reason"
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"
              />
              <button onClick={confirmCancel} disabled={cancelling} data-testid="cancel-order-submit" className="w-full bg-red-600 text-white rounded-full py-3.5 font-medium hover:bg-red-700 disabled:opacity-60">
                {cancelling ? "Cancelling..." : "Cancel order"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
